import qs from 'query-string';
import fetch from 'node-fetch';
import { getDefaultUgcParStats } from 'helpers/scores';
import { DEFAULT_LEVEL_MUSIC } from 'helpers/constants';
import { loadProgress } from 'helpers/player';
import { appendInputTelemetryToMetadata } from 'helpers/inputTelemetry';
import { getMultiplayerTelemetryContext } from 'helpers/multiplayerTelemetry';

const onDEV = typeof __DEV__ !== 'undefined';
const HOST = 'https://chesslike.net'; // onDEV ? 'http://[::1]:8000' : 'https://chesslike.net';

const PROD_NODE_HOST = 'https://server.chesslike.net';

const LOCAL_NODE_HOST = 'http://localhost:5000';

const NODE_HOST = onDEV ? LOCAL_NODE_HOST : PROD_NODE_HOST;

const getSocketHost = () =>
  (typeof window !== 'undefined' && window.SERVER_HOST) || NODE_HOST;

function readGreenworksValue(label, getter) {
  try {
    return getter();
  } catch (error) {
    return `${label} error: ${error.message}`;
  }
}

function installSteamDiagnosticLogs(greenworks) {
  if (!greenworks || window.__steamDiagnosticsInstalled) {
    return;
  }

  window.__steamDiagnosticsInstalled = true;

  const logSteamEvent = eventName => (...args) => {
    console.log(`[Steam diagnostics] ${eventName}`, ...args);
  };

  [
    'game-overlay-activated',
    'steam-servers-connected',
    'steam-servers-disconnected',
    'steam-server-connect-failure',
    'game-servers-connected',
    'game-servers-disconnected',
    'game-server-connect-failure',
    'steam-shutdown',
    'lobby-created',
    'lobby-enter',
    'lobby-invite',
    'lobby-join-requested'
  ].forEach(eventName => {
    if (greenworks.on) {
      greenworks.on(eventName, logSteamEvent(eventName));
    }
  });

  console.log('[Steam diagnostics] initialized', {
    appId: window.steamAppId,
    steamRunning: window.steamRunning,
    steamUserInfo: window.steamUserInfo,
    overlayEnabled:
      greenworks.isGameOverlayEnabled &&
      readGreenworksValue('isGameOverlayEnabled', () =>
        greenworks.isGameOverlayEnabled()
      ),
    greenworksVersion:
      typeof process !== 'undefined' &&
      process.versions &&
      process.versions.greenworks,
    nwVersion:
      typeof process !== 'undefined' && process.versions && process.versions.nw,
    chromiumVersion:
      typeof process !== 'undefined' &&
      process.versions &&
      process.versions.chromium,
    hasWindowRequire: typeof window.require === 'function',
    userAgent: window.navigator && window.navigator.userAgent
  });
}

export function ensureGreenworksInitialized() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!window.greenworks && typeof window.require === 'function') {
    try {
      window.greenworks = window.require('./greenworks');
    } catch (error) {
      console.error('Unable to require Greenworks:', error);
    }
  }

  if (!window.greenworks) {
    return null;
  }

  try {
    if (!window.steamRunning) {
      window.greenworks.init();
      window.steamAppId = window.greenworks.getAppId();
      window.steamRunning = window.greenworks.isSteamRunning();
    }
    if (!window.steamUserInfo) {
      window.steamUserInfo = window.greenworks.getSteamId();
    }
    if (
      window.steamUserInfo &&
      window.steamUserInfo.screenName &&
      window.steamUserInfo.staticAccountId
    ) {
      localStorage.setItem('chesslike-user', window.steamUserInfo.screenName);
      localStorage.setItem(
        'chesslike-steamid',
        window.steamUserInfo.staticAccountId
      );
    }
  } catch (error) {
    console.error('Unable to initialize Greenworks:', error);
    return null;
  }

  installSteamDiagnosticLogs(window.greenworks);

  return window.greenworks;
}

export function ensureSocketConnection() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available'));
  }

  window.SERVER_HOST = getSocketHost();

  if (window.socket) {
    if (window.socket.disconnected) {
      window.socket.open();
    }
    return Promise.resolve(window.socket);
  }

  const createSocket = () => {
    if (!window.io) {
      throw new Error('Socket.IO client did not load');
    }
    window.socket = window.io(window.SERVER_HOST);
    return window.socket;
  };

  if (window.io) {
    return Promise.resolve(createSocket());
  }

  if (!window.__socketIoLoadPromise) {
    window.__socketIoLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${window.SERVER_HOST}/socket.io/socket.io.js`;
      script.onload = () => {
        try {
          resolve(createSocket());
        } catch (error) {
          reject(error);
        }
      };
      script.onerror = () => {
        reject(new Error(`Unable to load Socket.IO from ${script.src}`));
      };
      document.head.appendChild(script);
    });
  }

  return window.__socketIoLoadPromise;
}

const isRegressionTestRun = () =>
  typeof window !== 'undefined' && window.dev?.regression_test === true;

const showGlobalAlertDialog = message => {
  if (typeof window === 'undefined') {
    return Promise.resolve(true);
  }

  const activeScene = window.game?.scene
    ?.getScenes(true)
    ?.slice()
    ?.reverse()
    ?.find(scene => scene.scene?.key !== 'ConfirmationDialogScene');

  if (activeScene?.scene) {
    return new Promise(resolve => {
      activeScene.scene.launch('ConfirmationDialogScene', {
        title: 'Notice',
        message,
        confirmLabel: 'OK',
        showCancel: false,
        resolve
      });
      activeScene.scene.bringToTop('ConfirmationDialogScene');
    });
  }

  window.alert?.(message);
  return Promise.resolve(true);
};

const parseEventMetadata = metadata => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

const getEventMetadata = eventData => {
  const metadata = appendInputTelemetryToMetadata({
    ...parseEventMetadata(eventData.metadata),
    ...(isRegressionTestRun() ? { regression_test: true } : {})
  });

  if (eventData.event_name !== 'level_start') {
    return Object.keys(metadata).length ? JSON.stringify(metadata) : undefined;
  }

  try {
    const {
      party,
      storyProgress,
      worldProgress,
      lastKnownPosition,
      stepCounter
    } = loadProgress();
    return JSON.stringify({
      ...metadata,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio
      },
      story_progress: storyProgress,
      world_progress: worldProgress,
      last_known_position: lastKnownPosition,
      step_counter: stepCounter,
      starting_party: (party || []).map(member => ({
        id: member.id,
        name: member.name,
        rank: member.rank,
        tileKey: member.tileKey,
        leader: !!member.leader,
        main: !!member.main,
        itemID: member.itemID ?? null
      }))
    });
  } catch (e) {
    return Object.keys(metadata).length
      ? JSON.stringify(metadata)
      : eventData.metadata;
  }
};

export function recordEvent(eventData, game) {
  // Ensure required fields are present
  if (!eventData.event_name) {
    console.error('recordEvent: Missing required fields (event_name)');
    return Promise.reject(new Error('Missing required fields'));
  }
  if (!game) {
    console.error('recordEvent: Missing required fields (game)');
    return Promise.reject(new Error('Missing required fields'));
  }
  const adaptedEventData = {
    user_id: game.player.name,
    session_id: game?.sessionID || 'not implemented',
    platform: 'not implemented',
    build_version: __COMMIT_HASH__,
    ...eventData,
    metadata: getEventMetadata(eventData)
  };
  return fetch(`${NODE_HOST}/api/metrics/record-event`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(adaptedEventData)
  })
    .then(res => {
      return res.json();
    })
    .catch(error => {
      console.error('Error recording event:', error);
    });
}

export function reportClientError(errorData) {
  if (isRegressionTestRun()) {
    return Promise.resolve({ success: true, ignored: true });
  }

  const errorMetadata =
    errorData.metadata && typeof errorData.metadata === 'object'
      ? errorData.metadata
      : {};
  const multiplayerContext = getMultiplayerTelemetryContext();
  const adaptedErrorData = {
    ...errorData,
    metadata: appendInputTelemetryToMetadata({
      ...errorMetadata,
      multiplayer: multiplayerContext
    })
  };

  return fetch(`${NODE_HOST}/api/metrics/report-error`, {
    method: 'POST',
    mode: 'cors',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(adaptedErrorData)
  })
    .then(res => {
      if (!res.ok) {
        console.warn(`Error report failed with status ${res.status}`);
        return null;
      }

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        return null;
      }

      return res.json();
    })
    .catch(error => {
      console.warn('Error reporting client error:', error);
    });
}

export function login(username, password, check = 'false') {
  return fetch(`${NODE_HOST}/api/login`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      check,
      username,
      password
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function recoverAccount(email) {
  return fetch(`${HOST}/api/user/recover.php`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      email
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function registerAccount(email, username, password, password2) {
  return fetch(`${HOST}/api/user/register.php`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      username,
      email,
      password,
      password2
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function saveWorldData(
  token,
  username,
  data,
  worldID = null,
  steamTicket = null
) {
  return writeWorldDataToLocalFile(data, worldID || 'world');
}

export function writeWorldDataToLocalFile(data, worldID = null) {
  return fetch(`${LOCAL_NODE_HOST}/write-world-data`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify({ data, worldID: worldID || 'world' })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return showGlobalAlertDialog(
        'World data saved locally. Rebuilding app. The page should refresh automatically after clicking ok here.'
      ).then(() => res);
    });
}

export function loadWorldData(version) {
  const endpoint = `${NODE_HOST}/api/world/loadWorld${
    version ? `/${version}` : ''
  }`;
  return fetch(endpoint, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      res.ground = JSON.parse(res.ground);
      res.belowPlayer = JSON.parse(res.below);
      res.decor = JSON.parse(res.decor);
      res.abovePlayer = JSON.parse(res.above);
      res.logic = JSON.parse(res.logic);
      return res;
    })
    .catch(error => {
      console.error('Error loading world data:', error);
      return null;
    });
}

export function loadWorldVersions() {
  return fetch(`${NODE_HOST}/api/world/worldVersions`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function writeZoneDataToLocalFile(zoneData) {
  fetch(`${LOCAL_NODE_HOST}/write-zone-data`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify(zoneData)
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function writeLevelEditorObjectDataToLocalFile(objectData) {
  fetch(`${LOCAL_NODE_HOST}/write-level-editor-object-data`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify(objectData)
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function writeWorldDoorDataToLocalFile(doorData) {
  fetch(`${LOCAL_NODE_HOST}/write-door-data`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify(doorData)
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

// window.loadWorld = loadWorldData;

export function writeLevelToLocalFile(
  data,
  id = 0,
  name = '',
  metadata,
  multiplayer = false,
  story = false
) {
  fetch(`${LOCAL_NODE_HOST}/write-story-level`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify({
      id: id.toString(),
      name,
      width: data.ground[0].length.toString(),
      height: data.ground.length.toString(),
      multiplayer: multiplayer ? '1' : '0',
      story: story ? '1' : '0',
      metadata: JSON.stringify(metadata),
      ground: JSON.stringify(data.ground),
      decor: JSON.stringify(data.decor),
      logic: JSON.stringify(data.logic),
      below: JSON.stringify(data.belowPlayer),
      above: JSON.stringify(data.abovePlayer)
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function writeCampaignDataToLocalFile(data) {
  fetch(`${LOCAL_NODE_HOST}/write-campaign-timelines`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json'
    },
    body: JSON.stringify(data)
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function saveStoryLevelData(
  data,
  id = 0,
  name = '',
  metadata,
  multiplayer = false,
  story = false
) {
  const { ground, belowPlayer: below, decor, abovePlayer: above, logic } = data;
  const token = localStorage.getItem('chesslike-token');
  const username = localStorage.getItem('chesslike-user');
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const userId = window.localStorage.getItem('chesslike-userid');
  const isDemo = __DEMO_MODE__;
  return fetch(`${NODE_HOST}/api/levels/save`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      ground: JSON.stringify(ground),
      below: JSON.stringify(below),
      decor: JSON.stringify(decor),
      above: JSON.stringify(above),
      logic: JSON.stringify(logic),
      metadata: JSON.stringify(metadata),
      token,
      username,
      userId,
      steamTicket,
      isDemo,
      w: data.ground[0].length,
      h: data.ground.length,
      multiplayer: multiplayer ? 1 : 0,
      story: story ? 1 : 0,
      theme: 0,
      id,
      name
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    })
    .catch(error => {
      return showGlobalAlertDialog('Error saving Level: ' + error).then(
        () => null
      );
    });
}

export function saveLevelData(
  data,
  id = 0,
  name = '',
  metadata = {},
  multiplayer = false
) {
  const { ground, belowPlayer: below, decor, abovePlayer: above, logic } = data;
  const metadataWithFlags = {
    ...(metadata || {}),
    multiplayer: !!multiplayer
  };

  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/saveMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      id, // keep using "id" until you rename to ugcLevelId
      name,
      w: data.ground[0].length,
      h: data.ground.length,
      ground: JSON.stringify(ground),
      below: JSON.stringify(below),
      decor: JSON.stringify(decor),
      above: JSON.stringify(above),
      logic: JSON.stringify(logic),
      metadata: JSON.stringify(metadataWithFlags),
      multiplayer: multiplayer ? 1 : 0
    })
  })
    .then(res => res.json())
    .catch(error => {
      return showGlobalAlertDialog('Error saving Level: ' + error).then(
        () => null
      );
    });
}

export function getPlayerMaps(author) {
  const endpoint = `${NODE_HOST}/api/levels/getPlayerMaps/${author}`;
  return fetch(endpoint, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      Object.keys(res).forEach(key => {
        let map = res[key];
        map.ground = JSON.parse(map.ground);
        map.belowPlayer = JSON.parse(map.below);
        map.decor = JSON.parse(map.decor);
        map.abovePlayer = JSON.parse(map.above);
        map.logic = JSON.parse(map.logic);
        map.metadata = map.metadata ? JSON.parse(map.metadata) : {};
      });
      return res;
    });
}

export function getUgcPlayerMaps() {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/getPlayerMaps`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      if (!Array.isArray(res)) {
        console.warn('Unexpected player maps response:', res);
        return [];
      }

      res.forEach(map => {
        map.ground = JSON.parse(map.ground);
        map.belowPlayer = JSON.parse(map.below);
        map.decor = JSON.parse(map.decor);
        map.abovePlayer = JSON.parse(map.above);
        map.logic = JSON.parse(map.logic);
        map.metadata = map.metadata ? JSON.parse(map.metadata) : {};
        map.startingMusic = map.metadata.startingMusic || DEFAULT_LEVEL_MUSIC;
      });
      return res;
    });
}

export function getUgcPlayerMapsList() {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/getPlayerMapsList`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      return res;
    });
}

export function getUgcPublishedMapsList(
  page = 1,
  limit = 20,
  sort = 'popular'
) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  const safePage = Math.max(1, Number(page || 1));
  const safeLimit = Math.max(1, Math.min(50, Number(limit || 20)));
  const safeSort = sort === 'recent' ? 'recent' : 'popular';
  const endpoint = `${NODE_HOST}/api/ugc/getPublishedMapsList?page=${safePage}&limit=${safeLimit}&sort=${safeSort}`;

  return fetch(endpoint, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      return res;
    });
}

export function getUgcPlayerNetworkPublishedMapsList(page = 1, limit = 20) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  const safePage = Math.max(1, Number(page || 1));
  const safeLimit = Math.max(1, Math.min(50, Number(limit || 20)));
  const endpoint = `${NODE_HOST}/api/ugc/getPlayerNetworkPublishedMapsList?page=${safePage}&limit=${safeLimit}`;

  return fetch(endpoint, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      return res;
    });
}

export function getPublishedUgcMap(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/getPublishedUgcMap/${ugcLevelId}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      if (res && (res.error || res.message) && !res.ground) {
        return res;
      }

      const map = { ...res };
      map.lvlData = {
        ground: JSON.parse(map.ground),
        belowPlayer: JSON.parse(map.below),
        decor: JSON.parse(map.decor),
        abovePlayer: JSON.parse(map.above),
        logic: JSON.parse(map.logic)
      };
      map.metadata = map.metadata ? JSON.parse(map.metadata) : {};
      map.equippedItems = map.metadata.equippedItems || [];
      map.startingMusic = map.metadata.startingMusic || DEFAULT_LEVEL_MUSIC;
      map.combat = true;
      map.dontCheckerBoard = true;
      map.parStats = map.parStats || getDefaultUgcParStats(map);

      return map;
    });
}

const normalizeUgcMapResponse = res => {
  if (res && (res.error || res.message) && !res.ground) {
    return res;
  }

  const map = { ...res };
  map.lvlData = {
    ground: JSON.parse(map.ground),
    belowPlayer: JSON.parse(map.below),
    decor: JSON.parse(map.decor),
    abovePlayer: JSON.parse(map.above),
    logic: JSON.parse(map.logic)
  };
  map.metadata = map.metadata ? JSON.parse(map.metadata) : {};
  map.equippedItems = map.metadata.equippedItems || [];
  map.startingMusic = map.metadata.startingMusic || DEFAULT_LEVEL_MUSIC;
  map.combat = true;
  map.dontCheckerBoard = true;
  map.parStats = map.parStats || getDefaultUgcParStats(map);

  return map;
};

export function getSharedUgcMap(shareCode) {
  return fetch(`${NODE_HOST}/api/ugc/shared/${encodeURIComponent(shareCode)}`, {
    method: 'GET',
    mode: 'cors'
  })
    .then(res => res.json())
    .then(normalizeUgcMapResponse);
}

export function createUgcShareLink(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/shareMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId
    })
  }).then(res => res.json());
}

export function likeUgcMap(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/likeMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId
    })
  })
    .then(res => res.json())
    .catch(error => {
      console.error('Error liking Level:', error);
      return null;
    });
}

export function reportUgcMap(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/reportMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId
    })
  })
    .then(res => res.json())
    .catch(error => {
      console.error('Error reporting Level:', error);
      return null;
    });
}

export function getUgcPlayerMap(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/getUgcMap/${ugcLevelId}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'x-steam-ticket': steamTicket,
      'x-is-demo': isDemo ? '1' : '0'
    }
  })
    .then(res => res.json())
    .then(res => {
      if (!Array.isArray(res)) {
        console.warn('Unexpected UGC map response:', res);
        return [];
      }

      res.forEach(map => {
        map.ground = JSON.parse(map.ground);
        map.belowPlayer = JSON.parse(map.below);
        map.decor = JSON.parse(map.decor);
        map.abovePlayer = JSON.parse(map.above);
        map.logic = JSON.parse(map.logic);
        map.metadata = map.metadata ? JSON.parse(map.metadata) : {};
        map.startingMusic = map.metadata.startingMusic || DEFAULT_LEVEL_MUSIC;
        map.dontCheckerBoard = true;
      });
      return res;
    });
}

export function beatUgcMap(ugcLevelId, versionId = null) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/beatMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId,
      ...(versionId ? { versionId } : {})
    })
  })
    .then(res => res.json())
    .catch(error => {
      return showGlobalAlertDialog('Error beating Level: ' + error).then(
        () => null
      );
    });
}

export function publishUgcMap(ugcLevelId, versionId = null) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/publishMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId,
      ...(versionId ? { versionId } : {})
    })
  })
    .then(res => res.json())
    .catch(error => {
      return showGlobalAlertDialog('Error publishing Level: ' + error).then(
        () => null
      );
    });
}

export function deleteUgcMap(ugcLevelId) {
  const steamTicket = localStorage.getItem('chesslike-steamTicket');
  const isDemo = __DEMO_MODE__;

  return fetch(`${NODE_HOST}/api/ugc/deleteMap`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      steamTicket,
      isDemo,
      ugcLevelId
    })
  })
    .then(res => res.json())
    .catch(error => {
      return showGlobalAlertDialog('Error deleting Level: ' + error).then(
        () => null
      );
    });
}

export function getPlayerCreatedLevels(page) {
  return fetch(`${NODE_HOST}/api/levels/getPlayerCreatedLevels/${page}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      Object.keys(res.maps).forEach(key => {
        let map = res.maps[key];
        map.lvlData = {};
        map.lvlData.ground = JSON.parse(map.ground);
        map.lvlData.belowPlayer = JSON.parse(map.below);
        map.lvlData.decor = JSON.parse(map.decor);
        map.lvlData.abovePlayer = JSON.parse(map.above);
        map.lvlData.logic = JSON.parse(map.logic);
        map.combat = true;
      });
      return res;
    });
}

export function getStoryMapByID(id) {
  return fetch(`${NODE_HOST}/api/levels/getStoryMapByID/${id}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      const map = res;
      map.lvlData = {};
      map.lvlData.ground = JSON.parse(map.ground);
      map.lvlData.belowPlayer = JSON.parse(map.below);
      map.lvlData.decor = JSON.parse(map.decor);
      map.lvlData.abovePlayer = JSON.parse(map.above);
      map.lvlData.logic = JSON.parse(map.logic);
      return map;
    })
    .catch(error => {
      console.error('Error fetching story map:', error);
      return null;
    });
}

export function getPlayerCreatedLevelsByIDs(ids) {
  return fetch(`${NODE_HOST}/api/levels/getPlayerCreatedLevelsByIDs`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      ids: JSON.stringify(ids)
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      Object.keys(res.maps).forEach(key => {
        let map = res.maps[key];
        map.lvlData = {};
        map.lvlData.ground = JSON.parse(map.ground);
        map.lvlData.belowPlayer = JSON.parse(map.below);
        map.lvlData.decor = JSON.parse(map.decor);
        map.lvlData.abovePlayer = JSON.parse(map.above);
        map.lvlData.logic = JSON.parse(map.logic);
        map.combat = true;
      });
      return res;
    });
}

export function getMultiplayerMaps() {
  return fetch(`${NODE_HOST}/api/levels/getMultiplayerMaps`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      Object.keys(res).forEach(key => {
        let map = res[key];
        if (map.metadata) {
          const metadata = JSON.parse(map.metadata);
          map.equippedItems = metadata.equippedItems;
        }
        map.ground = JSON.parse(map.ground);
        map.belowPlayer = JSON.parse(map.below);
        map.decor = JSON.parse(map.decor);
        map.abovePlayer = JSON.parse(map.above);
        map.logic = JSON.parse(map.logic);
        map.dontCheckerBoard = map.source === 'ugc';
      });
      return res;
    });
}

export function getStoryMaps() {
  return fetch(`${NODE_HOST}/api/levels/getStoryMaps`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => res);
}

export function getPlayerRecord(id) {
  return fetch(`${HOST}/api/v2/league/getPlayerRecord.php`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({ id })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function saveGameRecord(
  player1,
  player2,
  winner,
  loser,
  disconnected,
  history,
  map,
  ranked
) {
  return fetch(`${HOST}/api/v2/league/reportGame.php`, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    body: qs.stringify({
      player1,
      player2,
      winner,
      loser,
      disconnected: disconnected ? 1 : 0,
      history,
      map,
      ranked
    })
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
}

export function getHighScores(level_id, user_id) {
  return fetch(`${NODE_HOST}/api/scores/level/${level_id}/${user_id}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    })
    .catch(error => {
      console.error('Error fetching scores:', error);
      return { playerRank: {}, highScores: [], error: error };
    });
}

export function getAllScoresForPlayer(user_id) {
  const response = fetch(`${NODE_HOST}/api/scores/player/${user_id}`, {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    }
  })
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    })
    .catch(error => {
      console.error('Error fetching scores:', error);
      return [];
    });
  return response;
}

export function submitScore(
  player_id,
  steam_ticket,
  level_id,
  mode,
  score,
  time,
  turns,
  undos,
  items
) {
  try {
    return fetch(`${NODE_HOST}/api/scores/submit`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      },
      body: qs.stringify({
        player_id,
        steam_ticket,
        level_id,
        mode,
        score,
        time,
        turns,
        undos,
        items,
        isDemo: __DEMO_MODE__
      })
    })
      .then(res => {
        return res.json();
      })
      .then(res => {
        return res;
      });
  } catch (error) {
    console.error('Error submitting score:', error);
    return null;
  }
}

export const getSteamUserAuth = () => {
  return fetch(
    `https://partner.steam-api.com/ISteamUserAuth/AuthenticateUser/v1/`,
    {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      },
      body: qs.stringify()
    }
  )
    .then(res => {
      return res.json();
    })
    .then(res => {
      return res;
    });
};

const discordWebhook =
  'https://discordapp.com/api/webhooks/779057221912231947/sTBeh84weTwqyOAlh8YN6kkWgObMv6XJT1L0PVhcpeP0FvI9d-UnyfHQPBPAoTiEdLBG';

export function pingApps(message) {
  pingDiscord(message);
}

export function pingDiscord(message) {
  if (!onDEV && !isRegressionTestRun()) {
    try {
      return fetch(discordWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: message })
      });
    } catch (error) {
      console.error('Error pinging Discord:', error);
    }
  }
}

export function requestSteamAuthTicket() {
  return new Promise((resolve, reject) => {
    const greenworks = ensureGreenworksInitialized();
    if (!greenworks) {
      resolve(null);
      return;
    }

    greenworks.getAuthSessionTicket(steamTicketResponse => {
      if (!steamTicketResponse || !steamTicketResponse.ticket) {
        reject(new Error('Steam auth ticket response was empty'));
        return;
      }

      const steamTicket = steamTicketResponse.ticket;
      const hexTicket = Buffer.from(steamTicket, 'binary').toString('hex');
      localStorage.setItem('chesslike-steamTicket', hexTicket);
      resolve(hexTicket);
    }, reject);
  });
}

export function checkSteamUserRegistration(cb) {
  requestSteamAuthTicket()
    .then(hexTicket => {
      if (!hexTicket) {
        return;
      }
      const steamUserName =
        window.steamUserInfo && window.steamUserInfo.screenName;
      const steamUserId =
        window.steamUserInfo && window.steamUserInfo.staticAccountId;

      return fetch(`${NODE_HOST}/api/steam/userRegistration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          steamTicket: hexTicket,
          steamUserId,
          steamUserName,
          isDemo: __DEMO_MODE__
        })
      })
        .then(res => {
          return res.json();
        })
        .then(res => {
          cb(res);
        });
    })
    .catch(error => {
      console.error('Error registering Steam user:', error);
    });
}

// export function generateAiMap(query, success) {
//   return fetch(`${NODE_HOST}/api/levels/getAiGeneratedMap`, {
//     method: 'POST',
//     mode: 'cors',
//     headers: {
//       'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
//     },
//     body: qs.stringify({
//       query
//     })
//   })
//     .then(res => {
//       return res.json();
//     })
//     .then(res => {
//       // const adaptedMapData = adaptMapData(res);
//       success(res);
//       return res;
//     });
// }

// const adaptMapData = mapData => {
//   const adaptedLevelData = {
//     ...mapData
//   };

//   console.log('adapted level data:', adaptedLevelData);

//   return adaptedLevelData;
// };
